import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { AwardCode, BonusKind, PrivateView, PublicView, ScoreAward } from '@ringmaster/shared';
import {
  AWARD_META,
  BONUS_CAP,
  FULL_MOON_XP_MULTIPLIER,
  bonusXp,
  crossedLevel,
  isFullMoonNight,
  isXpOnly,
  levelProgress,
} from '@ringmaster/shared';
import { Glyph, type GlyphName } from './Glyph.js';
import { Button } from './Button.js';
import { LevelDiamond, tierOf } from './LevelDiamond.js';
import { useTiltGlare, GLARE_CSS } from './tiltGlare.js';
import { useSequence } from '../fx/sequencer.js';
import { getVisualTuning } from '../fx/visualTuning.js';
import { BurstLayer, burst, ParticleLayer, useReducedMotion } from '../fx/index.js';

/**
 * ScoreRevealModal — the ceremony that hands a player their points.
 *
 * Shown once at END, over whichever end screen the surface already renders, and
 * only to the LOCAL player: it itemises their own ledger (`PublicView.awards`
 * filtered to their id — see shared/scoring.ts), counts the total up as each row
 * lands, and walks the level bar along with it.
 *
 * Choreography is a `visualTuning` sequence (`SCORE_REVEAL`), never hardcoded ms
 * — MAKEOVER hard rule 4. The sequence carries three BEATS (verdict / bar / rows)
 * and four `*Ms` DURATION PARAMS the handlers consume:
 *   rowStaggerMs  gap between two ledger rows landing
 *   rowCountMs    how long the running total takes to catch up to a landed row
 *   crescendoMs   the final-total flourish
 *   continueMs    delay from the last row to the Continue button
 * The row cascade is scheduled from the `rows` beat + `rowStaggerMs` because the
 * ledger is 1..N rows long — a fixed beat map cannot name a variable row count.
 *
 * Reduced motion keeps the ORDER and the TIMING (repo-wide rule) and drops the
 * motion: no slam/blur/sweep (CSS), no bursts (the burst bus is already a no-op
 * under reduced motion), and count-ups snap to their target instead of easing.
 *
 * Never a cutscene the player is trapped in: a pointer anywhere fast-forwards the
 * whole cascade, and Continue is always the way out.
 *
 * THE LEDGER IS TWO LISTS. Rows that scored the game come first; the XP-only
 * grants (`isXpOnly` — shared/scoring.ts) come after a captioned rule, and the
 * footer then carries two labelled numbers instead of one. That is not cosmetic:
 * the room's leaderboard only ever saw the game rows, so a single "Total points"
 * under a mixed list stated a score nobody was ranked on.
 */

/** One ceremony glyph per award. Distinct within any single game's award set. */
const AWARD_GLYPH: Record<AwardCode, GlyphName> = {
  WIN: 'laurel',
  // The two profile-layer grants (shared/scoring.ts). Neither is minted by an
  // engine, so both can co-occur with ANY game's set and must not collide with
  // it: the Ringmaster's own hat for the welcome, and the NEW MOON — the turn of
  // the cycle — for the welcome back. (`moonCrescent` is the only other lunar
  // glyph in use, on a Whisper-only award; a disc and a crescent read apart.)
  FIRST_CURTAIN: 'topHat',
  // The SAME hat as FIRST_CURTAIN, and safe precisely because the two grants are
  // mutually exclusive — a first game ever is also a first game today, and
  // FIRST_CURTAIN wins that tie, so no ledger can ever show both. They are the
  // one house award wearing two names, and the Ringmaster's hat says so better
  // than a second-choice glyph: every icon left unused (`moonNew` is a bare
  // circle) reads as a missing asset next to drawn marks like the wolf or skull.
  ENCORE: 'topHat',
  // Blood Moon
  SCENT: 'wolfHead',
  GOOD_HUNT: 'claw',
  SEER_MARK: 'seerEye',
  ORACLE: 'scroll',
  MARTYR: 'skull',
  LONE_WOLF: 'raven',
  SILVER_TONGUE: 'quill',
  CLEAN_KILL: 'dagger',
  BETRAYAL: 'mask',
  BETRAYAL_LYNCH: 'noose',
  // One Night
  STOLEN_FANGS: 'dagger',
  JOKERS_MARK: 'seal',
  INNOCENT_FRAME: 'gallows',
  // The consolation for losing a guild's FIRST One Night — the full moon that
  // presided over it. Distinct within the One Night set (the only other lunar
  // glyph in use, `moonCrescent`, is Whisper-only) and it echoes the END
  // curtain's own moonFull mark, which is the beat this award is named after.
  CURTAIN_CALL: 'moonFull',
  // The Whisper
  FINDER: 'candle',
  SWIFT: 'moonCrescent',
  RIGHT_CALL: 'ballot',
  PERFECT_CRIME: 'mask',
};

/**
 * Which ledger row each welcome grant mints — the client half of the server's
 * `PlayerPublic.bonus` (shared/state.ts). One is a welcome, one a welcome back.
 */
const BONUS_AWARD = {
  first_game: 'FIRST_CURTAIN',
  daily: 'ENCORE',
} as const satisfies Record<BonusKind, AwardCode>;

/**
 * How long the END suspense curtain holds before the end screen appears.
 *
 * It lives HERE, next to the ceremony, because the two are one budget: the
 * curtain runs first, then `openDelayMs` buys reading time on top of it, and a
 * player sits through the sum. It used to be a 6000 duplicated in both end
 * screens, which — stacked with the delay — meant nine seconds of waiting on
 * every Blood Moon ending. Halved, and exported so the two surfaces can never
 * drift apart again.
 *
 * One Night skips it entirely: that game already rests a whole
 * ONENIGHT_RESOLVE phase on the tally with the verdict narration playing, so a
 * second suspense beat only makes a short game feel slow.
 */
export const END_CURTAIN_MS = 3000;

/** Fallbacks used only if a sequence key is missing from visualTuning.json. */
const FALLBACK = {
  /** How long the END screen is left alone before the ceremony covers it. */
  openDelayMs: 3000,
  rowStaggerMs: 620,
  rowCountMs: 560,
  crescendoMs: 700,
  continueMs: 420,
};

export interface ScoreRevealModalProps {
  /** The local player's awards, in ledger order (WIN first — see `awardsFor`). */
  awards: ScoreAward[];
  /** Did their side take the night? Drives gold vs blood, embers vs ash. */
  won: boolean;
  /** Dismiss — the parent unmounts the modal. */
  onDismiss: () => void;
  /**
   * LIFETIME XP the player carried INTO this game (`PlayerPublic.xp`, frozen for
   * the match — see shared/state.ts). `useScoreReveal` captures it once at mount
   * so a post-persist refresh cannot yank the bar mid-reveal. 0 for a seat with
   * no profile (every web/room-code player), which is the bar's old behaviour.
   */
  startPoints?: number;
  /** Panel width band. 'phone' is the default; 'desktop' is the Discord surface. */
  surface?: 'phone' | 'desktop';
}

export function ScoreRevealModal({
  awards,
  won,
  onDismiss,
  startPoints = 0,
  surface = 'phone',
}: ScoreRevealModalProps) {
  const reduced = useReducedMotion();
  const tilt = useTiltGlare<HTMLDivElement>();

  /**
   * The ledger in DISPLAY order: every row that scored the game first, then the
   * XP-only grants (`isXpOnly` — shared/scoring.ts). Cascade order, cumulative
   * sums, refs and bursts all index THIS list, so the divider below is a real
   * partition and not a line drawn through an interleaved list.
   *
   * A stable partition, so today's order survives inside each half: `awardsFor`
   * already puts WIN first and `useScoreReveal` already appends the grant last —
   * this only guarantees it rather than assuming it.
   */
  const rows = useMemo(() => {
    const game = awards.filter((a) => !isXpOnly(a.code));
    return game.length === awards.length ? awards : [...game, ...awards.filter((a) => isXpOnly(a.code))];
  }, [awards]);
  /** Index of the first XP-only row = how many rows scored the game. */
  const gameCount = useMemo(() => rows.filter((a) => !isXpOnly(a.code)).length, [rows]);
  const xpCount = rows.length - gameCount;

  // Running sums: cumulative[i] = points banked once row i has landed.
  const cumulative = useMemo(() => {
    let sum = startPoints;
    return rows.map((a) => (sum += a.points));
  }, [rows, startPoints]);
  const finalTotal = cumulative.length > 0 ? cumulative[cumulative.length - 1]! : startPoints;
  const earned = finalTotal - startPoints;

  const [revealed, setRevealed] = useState(0);
  const [displayTotal, setDisplayTotal] = useState(startPoints);
  // Mirror of `displayTotal` the rAF loop can read synchronously (React state is
  // not readable inside a callback captured before the render that set it).
  const displayRef = useRef(startPoints);
  const setTotal = useCallback((n: number) => {
    displayRef.current = n;
    setDisplayTotal(n);
  }, []);
  const [crescendo, setCrescendo] = useState(false);
  const [showContinue, setShowContinue] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const timers = useRef<number[]>([]);
  const rafRef = useRef<number | null>(null);
  // Latched by the skip: the sequencer owns the `rows` beat timer and cannot be
  // cancelled from here, so a skip taken BEFORE that beat lands would otherwise
  // let the cascade start over and walk the ledger back down from row 1.
  const skipped = useRef(false);

  const clearTimers = useCallback(() => {
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  // ── Count-up ────────────────────────────────────────────────────────────────
  // Eases the running total to `target` over `ms`. Reduced motion snaps (a
  // ticking counter IS the motion), which keeps the beat times intact.
  const countTo = useCallback(
    (target: number, ms: number) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (reduced || ms <= 0) {
        setTotal(target);
        return;
      }
      const from = displayRef.current;
      const t0 = performance.now();
      const step = (now: number) => {
        const p = Math.min(1, (now - t0) / ms);
        const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        setTotal(Math.round(from + (target - from) * eased));
        if (p < 1) rafRef.current = requestAnimationFrame(step);
        else rafRef.current = null;
      };
      rafRef.current = requestAnimationFrame(step);
      // Floor: rAF is SUSPENDED in a background tab, so a player who tabs away
      // mid-reveal would come back to a counter (and a level bar) frozen at the
      // wrong number. Timers still fire there, so this guarantees the value
      // lands even if `step` never runs once.
      timers.current.push(window.setTimeout(() => setTotal(target), ms + 60));
    },
    [reduced, setTotal],
  );

  // ── The sequence ────────────────────────────────────────────────────────────
  // `params` are read through a ref so the cue handlers (which are captured once)
  // always see the live tuning snapshot the sequence armed with.
  const paramsRef = useRef<Record<string, number>>({});

  const landRow = useCallback(
    (i: number, ms: number) => {
      if (skipped.current) return;
      setRevealed(i + 1);
      countTo(cumulative[i] ?? finalTotal, ms);
      const el = rowRefs.current[i];
      if (el) {
        const r = el.getBoundingClientRect();
        burst({
          preset: rows[i]?.code === 'WIN' ? 'goldBurst' : won ? 'sparkBurst' : 'ravenFeathers',
          x: (r.right - r.width * 0.12) / window.innerWidth,
          y: (r.top + r.height / 2) / window.innerHeight,
          count: rows[i]?.code === 'WIN' ? 44 : 20,
        });
        el.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
      }
      // A level-up is the loudest thing that can happen on this screen.
      if (crossedLevel(cumulative[i - 1] ?? startPoints, cumulative[i] ?? finalTotal)) {
        const bar = barRef.current?.getBoundingClientRect();
        if (bar) {
          burst({
            preset: 'goldBurst',
            x: (bar.left + bar.width / 2) / window.innerWidth,
            y: (bar.top + bar.height / 2) / window.innerHeight,
            count: 120,
          });
          burst({
            preset: 'laurelLeaves',
            x: (bar.left + bar.width / 2) / window.innerWidth,
            y: (bar.top + bar.height / 2) / window.innerHeight,
            count: 26,
          });
        }
      }
    },
    [rows, countTo, cumulative, finalTotal, reduced, startPoints, won],
  );

  const finish = useCallback(() => {
    setCrescendo(true);
    const p = paramsRef.current;
    timers.current.push(
      window.setTimeout(() => setShowContinue(true), p.continueMs ?? FALLBACK.continueMs),
    );
  }, []);

  const startCascade = useCallback(() => {
    if (skipped.current) return;
    const p = paramsRef.current;
    const stagger = p.rowStaggerMs ?? FALLBACK.rowStaggerMs;
    const countMs = p.rowCountMs ?? FALLBACK.rowCountMs;
    if (rows.length === 0) {
      finish();
      return;
    }
    for (let i = 0; i < rows.length; i++) {
      timers.current.push(window.setTimeout(() => landRow(i, countMs), i * stagger));
    }
    timers.current.push(window.setTimeout(finish, (rows.length - 1) * stagger + countMs));
  }, [rows.length, finish, landRow]);

  const seq = useSequence('SCORE_REVEAL', {
    enabled: true,
    onCue: { rows: startCascade },
  });
  paramsRef.current = seq.params;

  /** Fast-forward: every row lands at once, the total snaps, Continue appears. */
  const skipAhead = useCallback(() => {
    if (revealed >= rows.length && showContinue) return;
    skipped.current = true;
    clearTimers();
    setRevealed(rows.length);
    setTotal(finalTotal);
    setCrescendo(true);
    setShowContinue(true);
  }, [rows.length, clearTimers, finalTotal, revealed, setTotal, showContinue]);

  const complete = revealed >= rows.length;
  /**
   * The clamp notice. ONLY real game bonuses count toward it: WIN is the floor of
   * the invariant and the XP-only grants are outside `BONUS_CAP` entirely
   * (shared/scoring.ts `clampAwards` never shaves either). Summing every non-WIN
   * row instead — which this did — let a 100-point grant push a player with 20
   * points of actual bonuses past 150 and printed "bonus capped at 150" at them.
   */
  const capped =
    rows
      .filter((a) => a.code !== 'WIN' && !isXpOnly(a.code))
      .reduce((s, a) => s + a.points, 0) >= BONUS_CAP;
  /** What the ROOM's leaderboard saw: the game rows, and only once they land. */
  const gameEarned = rows.reduce(
    (s, a, i) => (isXpOnly(a.code) || i >= revealed ? s : s + a.points),
    0,
  );
  const lifetimeEarned = complete ? earned : Math.max(0, displayTotal - startPoints);
  const level = levelProgress(displayTotal);
  const levelUp = crossedLevel(startPoints, finalTotal);
  // The whole reveal wears the tier of the level it is standing in, so a crossing
  // that changes band repaints bar, numerals and call-out together as the
  // count-up walks `level.level` past the boundary. Fed to the CSS as custom
  // properties: the stylesheet is one static string shared by every instance.
  const tier = tierOf(level.level);
  const tierVars = {
    '--srm-tier': tier.border,
    '--srm-tier-inner': tier.inner,
    '--srm-tier-text': tier.text,
    '--srm-tier-glow': tier.glow,
  } as CSSProperties;
  const accent = won ? 'var(--gold-5, #e6b866)' : 'var(--blood-6, #ff5a4f)';

  return (
    <div
      className="srm-overlay"
      data-won={won ? '1' : '0'}
      data-surface={surface}
      role="dialog"
      aria-modal="true"
      aria-label={won ? 'Victory — your score' : 'Defeat — your score'}
      onPointerDown={skipAhead}
    >
      <BurstLayer />
      {/* Gold embers over a win, drifting ash over a loss. */}
      <ParticleLayer
        preset={won ? 'embers' : 'ash'}
        intensity={won ? 0.9 : 0.6}
        countScale={surface === 'desktop' ? 1.6 : 1}
        sizeScale={surface === 'desktop' ? 1.4 : 1}
      />

      <div className="srm-scene rm-tilt-scene">
        <div
          ref={tilt.ref}
          onPointerMove={tilt.onPointerMove}
          onPointerLeave={tilt.onPointerLeave}
          className={`srm-panel rm-tilt srm-${surface}`}
        >
          <div className="rm-glare" />

          <div className="srm-body">
            {/* ── Verdict ─────────────────────────────────────────────────── */}
            <div className={`srm-verdict ${seq.has('verdict') ? 'in' : ''}`}>
              {/* The word carries the verdict on its own — the laurel/skull
                  above it repeated what it already said, and the WIN row's own
                  laurel sits a few px below. */}
              <h2 className="srm-verdict-word">{won ? 'VICTORY' : 'DEFEAT'}</h2>
            </div>

            {/* ── Level progression ───────────────────────────────────────── */}
            <div className={`srm-level ${seq.has('bar') ? 'in' : ''}`} style={tierVars}>
              <div className="srm-level-caps">
                <span className="srm-cap">
                  {/* The crossing is called out HERE, not on the total row: a
                      strong game levels up most of the time early on, so
                      hijacking the total's label would have hidden "Total
                      points" almost always. This costs no extra height. */}
                  <span className={`srm-cap-label ${levelUp && crescendo ? 'up' : ''}`}>
                    {levelUp && crescendo ? 'Level up' : 'Current level'}
                  </span>
                  {/* The badge is re-minted as the count-up walks `level.level`,
                      so a crossing visibly re-strikes it under the burst that
                      already fires on the bar. */}
                  <LevelDiamond level={level.level} size={34} glow />
                </span>
                <span className="srm-cap right">
                  {/* Dim, but already wearing the tier it WILL be: when the next
                      level crosses a band the preview changes colour, which is
                      what turns "one more level" into a reward you can see. */}
                  <LevelDiamond level={level.nextLevel} size={34} dim />
                  <span className="srm-cap-label">Next level</span>
                </span>
              </div>
              <div className="srm-bar" ref={barRef}>
                <span className="srm-bar-fill" style={{ transform: `scaleX(${level.fraction})` }} />
              </div>
              <p className="srm-bar-numerals">
                {/* Points INTO the current level, not the lifetime total — the
                    denominator beside it is this level's requirement. Identical
                    to `displayTotal` until a carried-over total exists. */}
                <span className="now">{level.intoLevel}</span>
                <span className="sep">|</span>
                <span className="req">{level.required}</span>
              </p>
            </div>

            {/* ── The ledger ──────────────────────────────────────────────── */}
            <div className="srm-ledger" ref={listRef}>
              {/* An empty ledger is a real outcome, not a missing one: with no
                  participation award a losing seat can genuinely bank nothing.
                  Say so, rather than leaving a blank gap above a +0 total. */}
              {rows.length === 0 && (
                <p className="srm-empty in">Nothing banked this game.</p>
              )}
              {rows.map((a, i) => {
                const meta = AWARD_META[a.code];
                const hero = a.code === 'WIN';
                return (
                  <Fragment key={`${a.code}-${i}`}>
                    {/* The partition. Sits exactly where the game rows stop, and
                        lands WITH the first grant row (never before it), so the
                        cascade still reads as one falling list. */}
                    {i === gameCount && (
                      <div className={`srm-split ${revealed > gameCount ? 'in' : ''}`}>
                        <span className="srm-split-rule" aria-hidden />
                        <span className="srm-split-label">Lifetime XP only</span>
                        <span className="srm-split-rule" aria-hidden />
                      </div>
                    )}
                    <div
                      ref={(el) => {
                        rowRefs.current[i] = el;
                      }}
                      className={`srm-row ${hero ? 'hero' : ''} ${isXpOnly(a.code) ? 'xp' : ''} ${i < revealed ? 'in' : ''}`}
                    >
                      <span className="srm-row-sweep" aria-hidden />
                      <span className="srm-row-glyph">
                        <Glyph name={AWARD_GLYPH[a.code]} size={hero ? 30 : 22} tone={hero ? 'gold' : won ? 'gold' : 'bone'} />
                      </span>
                      <span className="srm-row-text">
                        <span className="srm-row-label">{meta.label}</span>
                        <span className="srm-row-desc">{meta.description}</span>
                      </span>
                      <span className="srm-row-points">+{a.points}</span>
                    </div>
                  </Fragment>
                );
              })}
            </div>

            {/* ── Total ───────────────────────────────────────────────────────
                Two numbers whenever a grant is on the ledger, because there ARE
                two: the room's leaderboard only ever saw the game rows, while the
                bar above walked the whole pile. One labelled number each, on one
                line, so the panel does not grow — the Discord surface is ~720px
                tall. With no grant the two are the same number and it stays the
                single "Total points" row it has always been. */}
            {xpCount > 0 ? (
              <div className={`srm-total split ${crescendo ? 'peak' : ''}`}>
                <span className="srm-total-part">
                  <span className="srm-total-label">Game score</span>
                  <span className="srm-total-game">+{gameEarned}</span>
                </span>
                <span className="srm-total-part right">
                  <span className="srm-total-label">Lifetime XP</span>
                  <span className="srm-total-value">+{lifetimeEarned}</span>
                </span>
              </div>
            ) : (
              <div className={`srm-total ${crescendo ? 'peak' : ''}`}>
                <span className="srm-total-label">Total points</span>
                <span className="srm-total-value">+{lifetimeEarned}</span>
              </div>
            )}
            {/* The clamp is part of the truth: say so rather than letting the
                arithmetic look wrong (shared/scoring.ts BONUS_CAP). */}
            {capped && (
              <p className={`srm-capped ${complete ? 'in' : ''}`}>Bonus capped at {BONUS_CAP}</p>
            )}
          </div>

          {/* ── Way out ───────────────────────────────────────────────────── */}
          <div className={`srm-foot ${showContinue ? 'in' : ''}`}>
            {/* Full-moon note ONLY — the displayed points are NOT doubled (the
                room's scoreboard never is; only the persisted lifetime XP earns
                the multiplier server-side), so this is a caption, not math.
                Calendar truth (isFullMoonNight), same deliberate divergence
                from a dev FULL_MOON_FORCE as ui/FullMoonBanner.tsx. */}
            {showContinue && isFullMoonNight(new Date()) && (
              <p className="srm-fullmoon">Full Moon — XP ×{FULL_MOON_XP_MULTIPLIER}</p>
            )}
            {showContinue ? (
              <Button variant="primary" tone={won ? 'gold' : 'blood'} onClick={onDismiss}>
                Continue
              </Button>
            ) : (
              <p className="srm-skip">Tap to skip</p>
            )}
          </div>
        </div>
      </div>

      <style>{GLARE_CSS}</style>
      <style>{CSS}</style>
    </div>
  );
}

/**
 * The local player's ledger, WIN first (the hero row) then the bonus rows in the
 * order the engine minted them. Returns [] when there is nothing to show — the
 * caller renders no modal at all (spectators have no `playerId`, a null `awards`
 * means any phase but END).
 */
export function awardsFor(publicView: PublicView, privateView: PrivateView | null): ScoreAward[] {
  if (!publicView.awards || !privateView) return [];
  const mine = publicView.awards.filter((a) => a.playerId === privateView.playerId);
  return [...mine].sort((a, b) => (a.code === 'WIN' ? -1 : 0) - (b.code === 'WIN' ? -1 : 0));
}

/**
 * useScoreReveal — "let them READ it, show it once, let them close it" for both
 * end surfaces. `dismiss` closes it for good (the same END view keeps
 * re-broadcasting, so this must not re-open).
 *
 * It owns the three things BOTH surfaces must get identically:
 *
 *  - the OPEN DELAY. The ceremony is a full-screen overlay, so opening it the
 *    instant the end screen mounts means the player never learns what happened —
 *    who died, the full role reveal, how everyone voted — before the points land
 *    on top of it. `openDelayMs` (visualTuning `SCORE_REVEAL`, ~3s) buys that
 *    read. Reduced motion KEEPS the delay: the repo rule preserves timing and
 *    drops only movement, and this beat is reading time, not motion.
 *  - `startPoints`, the lifetime XP the bar starts from, captured ONCE at mount.
 *    `PlayerPublic.xp` is already frozen for the match server-side; freezing it
 *    again here means even a mid-ceremony roster patch cannot yank the bar.
 *  - the WELCOME GRANT row. No engine mints either grant (they are lifetime
 *    state, not game awards — shared/levels.ts), so the client appends the one
 *    the server named and it lands as a real ceremony row: its own burst, its
 *    own step of the bar.
 *
 * `armed` is when the end screen is actually ON SCREEN. It defaults to true, and
 * both shipped surfaces pass `!curtain`: the delay is measured from the moment
 * there is something to read, not from END. Blood Moon holds a 6s suspense
 * curtain first, which would otherwise swallow the whole delay and land the
 * ceremony on the same frame the end screen appeared — the exact bug.
 */
export function useScoreReveal(
  publicView: PublicView,
  privateView: PrivateView | null,
  armed = true,
) {
  const me = privateView
    ? (publicView.players.find((p) => p.id === privateView.playerId) ?? null)
    : null;

  const [startPoints] = useState(() => me?.xp ?? 0);
  /**
   * WHICH grant is owed is the SERVER's call, not an inference: it is decided
   * inside the persist transaction and frozen on `PlayerPublic.bonus` for the
   * match (shared/state.ts). The client cannot derive it — "first game today"
   * needs a last-played timestamp no view carries — and must not try, because
   * what is shown here has to be what the DB pays. Frozen again at mount for the
   * same reason `startPoints` is: a mid-ceremony roster patch must not add or
   * retract a row halfway through the cascade.
   */
  const [bonus] = useState<BonusKind | null>(() => me?.bonus ?? null);

  const awards = useMemo(() => {
    const rows = awardsFor(publicView, privateView);
    // `publicView.awards` is the END gate `awardsFor` applies — re-checked here
    // so the grant can never open a ceremony before the game is over, while a
    // first-timer whose engine ledger is EMPTY still gets one.
    if (!bonus || !publicView.awards || !privateView) return rows;
    return [
      ...rows,
      {
        playerId: privateView.playerId,
        code: BONUS_AWARD[bonus],
        // Never restated: the two grants happen to be worth the same today and
        // must be free to diverge without this row lying about it.
        points: bonusXp(bonus),
        // Same absent-gameId default the rest of the client uses. The row is
        // game-agnostic (both grants list every game in AWARD_META); this field
        // only tags which table it was earned at.
        gameId: publicView.gameId ?? 'bloodmoon',
      },
    ];
  }, [publicView, privateView, bonus]);

  const [dismissed, setDismissed] = useState(false);
  const dismiss = useCallback(() => setDismissed(true), []);

  // Opens for any SEATED player once the game is over — deliberately NOT gated on
  // having earned something. It used to be, back when `AT_THE_TABLE` gave every
  // seat a row so the ledger was never empty; with that award gone, a player who
  // banked nothing got no ceremony at all, and whether you saw one after a loss
  // came down to whether you happened to pick up a 20-point bonus. It also meant
  // the players most in need of "here is where you stand" — the ones who scored
  // nothing — were the only ones never shown their level bar.
  //
  // `publicView.awards` (non-null only at END, and only once the table's ledger
  // exists) is the gate instead; `privateView` keeps it off spectators.
  const seated = privateView != null && publicView.awards != null;

  /**
   * The delay, latched: once it has elapsed it never un-elapses, so the END view
   * re-broadcasting (or the roster patching mid-ceremony) cannot restart the
   * wait or re-arm a modal the player already dismissed.
   */
  const [waited, setWaited] = useState(false);
  // The SAME gate `open` uses, deliberately: arming the timer on `awards.length`
  // instead re-broke the empty-ledger case from the other side — the modal would
  // never open because the wait never started.
  const ready = armed && seated;
  useEffect(() => {
    if (!ready || waited) return;
    // Live tuning snapshot, read at arm time — the same contract useSequence
    // uses. A missing/non-numeric key falls back rather than opening instantly.
    const tuned = getVisualTuning().sequences.SCORE_REVEAL?.openDelayMs;
    const ms = typeof tuned === 'number' ? tuned : FALLBACK.openDelayMs;
    const t = window.setTimeout(() => setWaited(true), ms);
    return () => clearTimeout(t);
  }, [ready, waited]);

  return { awards, startPoints, open: waited && seated && !dismissed, dismiss };
}

const CSS = `
.srm-overlay {
  position: fixed; inset: 0; z-index: 900;
  display: flex; align-items: center; justify-content: center;
  padding: clamp(10px, 3vw, 28px);
  padding-top: max(clamp(10px, 3vw, 28px), env(safe-area-inset-top, 0px));
  padding-bottom: max(clamp(10px, 3vw, 28px), env(safe-area-inset-bottom, 0px));
  background:
    radial-gradient(120% 70% at 50% 0%, rgba(90, 58, 18, 0.28) 0%, rgba(0,0,0,0) 60%),
    rgba(4, 3, 4, 0.88);
  backdrop-filter: blur(14px) saturate(0.85);
  -webkit-backdrop-filter: blur(14px) saturate(0.85);
  animation: srm-fade 420ms var(--ease-standard) both;
}
.srm-overlay[data-won="0"] {
  background:
    radial-gradient(120% 70% at 50% 0%, rgba(96, 12, 20, 0.30) 0%, rgba(0,0,0,0) 60%),
    rgba(4, 2, 2, 0.90);
}
/* align-items:center, not the flex default stretch — the panel must HUG its
   ledger (a 2-row loss is a small card) instead of growing to the viewport and
   stranding the total at the bottom of an empty box. The DEFINITE height is what
   makes the panel's own max-height:100% resolve — against an auto-height parent
   a percentage max-height is ignored, and a long ledger overflowed the screen. */
.srm-scene { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }

.srm-panel {
  position: relative;
  width: 100%;
  max-height: 100%;
  display: flex; flex-direction: column;
  overflow: hidden;
  border-radius: var(--radius-card);
  border: 1px solid var(--gold-2, #7a4c1e);
  background: linear-gradient(180deg, rgba(26, 17, 9, 0.97) 0%, rgba(10, 7, 6, 0.98) 100%);
  box-shadow: var(--shadow-card), 0 0 70px rgba(230, 184, 102, 0.14);
  animation: srm-panel-in 520ms var(--ease-standard) both;
}
.srm-overlay[data-won="0"] .srm-panel {
  border-color: var(--blood-2, #66101f);
  background: linear-gradient(180deg, rgba(24, 10, 12, 0.97) 0%, rgba(9, 6, 6, 0.98) 100%);
  box-shadow: var(--shadow-card), 0 0 70px rgba(153, 0, 0, 0.18);
}
.srm-panel.srm-phone { max-width: 460px; }
.srm-panel.srm-desktop { max-width: 620px; }

.srm-body {
  position: relative; z-index: 2;
  display: flex; flex-direction: column;
  min-height: 0;
  padding: clamp(14px, 3.6vw, 22px) clamp(15px, 4vw, 24px) 0;
  gap: clamp(9px, 2.4vw, 16px);
}

/* ── Verdict ──────────────────────────────────────────────────────────────── */
.srm-verdict {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  opacity: 0;
}
.srm-verdict.in { animation: srm-slam 620ms var(--ease-dramatic) both; }
.srm-verdict-word {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(32px, 9.6vw, 52px);
  line-height: 0.95;
  letter-spacing: 0.05em;
  background: linear-gradient(180deg, var(--gold-6, #f7e3b0) 0%, var(--gold-5, #e6b866) 45%, var(--gold-2, #7a4c1e) 100%);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
  filter: drop-shadow(0 4px 26px rgba(230, 184, 102, 0.35));
}
/* The Discord surface is WIDE but only ~720px tall, and the whole ledger has to
   land without scrolling. It gets more horizontal room and a tighter vertical
   rhythm, not a bigger version of the phone. */
.srm-desktop .srm-verdict-word { font-size: clamp(38px, 3.6vw, 46px); }
.srm-desktop .srm-body { gap: 11px; padding-top: 16px; }
.srm-desktop .srm-foot { min-height: 66px; padding: 12px 24px 16px; }
.srm-desktop .srm-row-desc { font-size: 12.5px; }
.srm-desktop .srm-row.hero { padding: 10px 13px; }
.srm-desktop .srm-total { padding-top: 10px; }
/* The Discord iframe is short — every px of overlay padding is a px the ledger
   loses, and this surface has no notch/home-bar to clear. */
.srm-overlay[data-surface="desktop"] { padding: 12px; }
.srm-overlay[data-won="0"] .srm-verdict-word {
  background: linear-gradient(180deg, #e8d9d9 0%, var(--blood-5, #c22f3e) 55%, var(--blood-2, #66101f) 100%);
  -webkit-background-clip: text; background-clip: text;
  filter: drop-shadow(0 4px 26px rgba(153, 0, 0, 0.45));
}

/* ── Level bar ────────────────────────────────────────────────────────────── */
.srm-level { opacity: 0; }
.srm-level.in { animation: srm-rise 480ms var(--ease-standard) both; }
.srm-level-caps { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 6px; }
.srm-cap { display: flex; flex-direction: column; gap: 2px; }
.srm-cap.right { align-items: flex-end; }
.srm-cap-label {
  font-family: var(--font-ui); font-size: 9px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.18em;
  color: var(--text-tertiary);
}
/* Tier-tinted: the call-out is the loudest word on the screen and has to match
   the badge that just re-minted beside it. */
.srm-cap-label.up {
  color: var(--srm-tier-text, var(--gold-6, #f7e3b0));
  text-shadow: 0 0 16px var(--srm-tier-glow, rgba(230, 184, 102, 0.7));
  animation: srm-breathe 1.8s ease-in-out infinite;
}

/* One track, one fill. No segments — points are a continuous quantity and the
   chunked bar read as a stylised HUD meter rather than progress. */
.srm-bar {
  position: relative; height: 14px; overflow: hidden; border-radius: 2px;
  background: rgba(255, 255, 255, 0.055);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}
.srm-desktop .srm-bar { height: 16px; }
.srm-bar-fill {
  position: absolute; inset: 0;
  transform-origin: left center;
  background: linear-gradient(180deg, var(--srm-tier-text, #f7e3b0) 0%, var(--srm-tier, #c9923f) 55%, var(--srm-tier-inner, #7a4c1e) 100%);
  box-shadow: 0 0 14px var(--srm-tier-glow, rgba(230, 184, 102, 0.55));
  /* The TIER's colour on a defeat too — the rest of the loss treatment stays
     blood, but points are points and the bar has to mean the same thing either
     way (this was gold before tiers existed; the win/loss split is unchanged,
     the tier simply replaces the gold).
     NO transition here on purpose: the fill is driven off displayTotal, which
     is already an eased rAF count-up — a CSS transition on top of a value that
     changes every frame restarts every frame and can wedge part-filled. The
     count-up IS the animation; reduced motion snaps it, which is correct. */
}
.srm-bar-numerals {
  margin: 6px 0 0; text-align: center;
  font-family: var(--font-ui); font-size: 15px; font-weight: 600;
  font-variant-numeric: tabular-nums; letter-spacing: 0.04em;
}
.srm-bar-numerals .now { color: var(--srm-tier-text, #f7e3b0); }
.srm-bar-numerals .sep { color: rgba(255, 255, 255, 0.22); margin: 0 8px; }
.srm-bar-numerals .req { color: var(--text-tertiary); }

/* ── Ledger ───────────────────────────────────────────────────────────────── */
.srm-ledger {
  display: flex; flex-direction: column; gap: 6px;
  min-height: 0; overflow-y: auto; overscroll-behavior: contain;
  /* No negative margin: the rows line up with the total's rule and the bar above
     rather than hanging 4px past them. The scrollbar is 4px and only appears on
     an overflowing ledger, where it overlays the very edge of a row's fill. */
  margin: 0; padding: 8px 0;
  /* A long ledger has to scroll on a phone; the edge fade makes the clipped row
     read as "more below" instead of as a broken card. */
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 10px, #000 calc(100% - 14px), transparent 100%);
  mask-image: linear-gradient(180deg, transparent 0, #000 10px, #000 calc(100% - 14px), transparent 100%);
  scrollbar-width: thin;
  scrollbar-color: rgba(230, 184, 102, 0.35) transparent;
}
.srm-ledger::-webkit-scrollbar { width: 4px; }
.srm-ledger::-webkit-scrollbar-track { background: transparent; }
.srm-ledger::-webkit-scrollbar-thumb { background: rgba(230, 184, 102, 0.3); border-radius: 2px; }
.srm-overlay[data-won="0"] .srm-ledger { scrollbar-color: rgba(194, 47, 62, 0.35) transparent; }
.srm-overlay[data-won="0"] .srm-ledger::-webkit-scrollbar-thumb { background: rgba(194, 47, 62, 0.3); }
.srm-row {
  position: relative; overflow: hidden;
  /* flex-shrink:0 — rows are flex children of the ledger and would otherwise
     compress (clipping their descriptions) instead of letting it scroll. */
  flex: 0 0 auto;
  display: grid; grid-template-columns: 30px 1fr auto;
  align-items: center; column-gap: 11px;
  padding: 8px 12px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.028);
  opacity: 0; transform: translateY(14px);
}
.srm-row.in { animation: srm-row-in 460ms var(--ease-spring) both; }
.srm-row.hero {
  grid-template-columns: 36px 1fr auto;
  padding: 12px 13px;
  border-color: var(--gold-3, #a9762f);
  background: linear-gradient(90deg, rgba(230, 184, 102, 0.16) 0%, rgba(230, 184, 102, 0.05) 100%);
  box-shadow: inset 3px 0 0 var(--gold-5, #e6b866);
}
.srm-overlay[data-won="0"] .srm-row.hero {
  border-color: var(--blood-3, #802636);
  background: linear-gradient(90deg, rgba(153, 0, 0, 0.2) 0%, rgba(153, 0, 0, 0.05) 100%);
  box-shadow: inset 3px 0 0 var(--blood-5, #c22f3e);
}
.srm-row-glyph { display: flex; justify-content: center; align-items: center; }
.srm-row-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.srm-row-label {
  font-family: var(--font-display); font-weight: 600;
  font-size: 20px; line-height: 1.1; letter-spacing: 0.015em;
  color: rgba(255, 255, 255, 0.92);
}
.srm-row.hero .srm-row-label { font-size: 24px; color: var(--gold-6, #f7e3b0); }
.srm-overlay[data-won="0"] .srm-row.hero .srm-row-label { color: #f0e2e2; }
.srm-row-desc {
  font-family: var(--font-ui); font-size: 12px; line-height: 1.28;
  color: var(--text-tertiary);
}
.srm-row-points {
  font-family: var(--font-ui); font-weight: 700; font-size: 18px;
  font-variant-numeric: tabular-nums; letter-spacing: 0.01em;
  color: var(--gold-5, #e6b866);
}
.srm-row.hero .srm-row-points { font-size: 26px; color: var(--gold-6, #f7e3b0); }
.srm-overlay[data-won="0"] .srm-row-points { color: rgba(255, 255, 255, 0.78); }
.srm-overlay[data-won="0"] .srm-row.hero .srm-row-points { color: var(--blood-6, #ff5a4f); }
.srm-row-sweep {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(100deg, transparent 30%, rgba(255, 240, 200, 0.30) 50%, transparent 70%);
  transform: translateX(-110%);
}
.srm-row.in .srm-row-sweep { animation: srm-sweep 760ms var(--ease-standard) 90ms both; }
.srm-overlay[data-won="0"] .srm-row-sweep {
  background: linear-gradient(100deg, transparent 30%, rgba(255, 200, 200, 0.18) 50%, transparent 70%);
}
/* Gold is the leaderboard's colour on this screen, so the rows that bought no
   place on it do not wear it. Everything else about the row is unchanged — this
   is the same card, not a second kind of row. */
.srm-row.xp .srm-row-points { color: var(--text-secondary); }

/* ── The partition ────────────────────────────────────────────────────────────
   A rule with a caption, not a card: it has to read as a BREAK in the list, and
   anything with a border/background would read as one more entry in it. Same
   9px uppercase caption as the bar's own labels — no new type in this view. */
.srm-split {
  flex: 0 0 auto;
  display: flex; align-items: center; gap: 8px;
  padding: 2px 2px 0;
  opacity: 0;
}
.srm-split.in { animation: srm-rise 420ms var(--ease-standard) both; }
.srm-split-rule { flex: 1 1 auto; height: 1px; background: rgba(230, 184, 102, 0.2); }
.srm-split-label {
  flex: 0 0 auto; white-space: nowrap;
  font-family: var(--font-ui); font-size: 9px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.18em;
  color: var(--text-tertiary);
}
.srm-overlay[data-won="0"] .srm-split-rule { background: rgba(194, 47, 62, 0.22); }

/* ── Total ────────────────────────────────────────────────────────────────── */
.srm-total {
  display: flex; align-items: baseline; justify-content: space-between;
  padding: 10px 2px 0;
  border-top: 1px solid rgba(230, 184, 102, 0.18);
}
.srm-overlay[data-won="0"] .srm-total { border-top-color: rgba(194, 47, 62, 0.2); }
/* Two labelled numbers on ONE line — the split costs the panel a label's height
   (~12px), not a whole extra row. */
.srm-total.split { align-items: flex-end; }
.srm-total-part { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.srm-total-part.right { align-items: flex-end; }
/* Deliberately smaller than .srm-total-value: the big animated number is the one
   the bar above is walking. Same gold as the game rows it sums, one step down
   from the total's gold-6 — no new colour, just the existing ramp. */
.srm-total-game {
  font-family: var(--font-ui); font-weight: 700; font-size: 22px;
  font-variant-numeric: tabular-nums;
  color: var(--gold-5, #e6b866);
}
.srm-overlay[data-won="0"] .srm-total-game { color: rgba(255, 255, 255, 0.78); }
.srm-total-label {
  font-family: var(--font-ui); font-size: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.2em;
  color: var(--text-tertiary);
}
.srm-total-value {
  font-family: var(--font-ui); font-weight: 700; font-size: 30px;
  font-variant-numeric: tabular-nums;
  color: var(--gold-6, #f7e3b0);
  text-shadow: 0 0 22px rgba(230, 184, 102, 0.35);
  transition: transform 300ms var(--ease-spring), text-shadow 300ms var(--ease-standard);
}
.srm-total.peak .srm-total-value { animation: srm-peak 700ms var(--ease-spring) both; }
.srm-overlay[data-won="0"] .srm-total-value { color: #f0e2e2; text-shadow: 0 0 22px rgba(194, 47, 62, 0.4); }

/* Quiet on purpose — it reports the ABSENCE of a ledger, and must not out-shout
   the level bar above it, which is the thing worth reading on that screen. */
.srm-empty {
  margin: 10px 0 2px; text-align: center;
  font-family: var(--font-ui); font-size: 13px; letter-spacing: 0.04em;
  color: var(--text-tertiary);
  opacity: 0;
}
.srm-empty.in { animation: srm-rise 460ms var(--ease-standard) both; }

.srm-capped {
  margin: 6px 0 0; text-align: right;
  font-family: var(--font-ui); font-size: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.16em;
  color: var(--gold-3, #a9762f);
  opacity: 0;
}
.srm-capped.in { animation: srm-rise 420ms var(--ease-standard) both; }
.srm-overlay[data-won="0"] .srm-capped { color: var(--blood-5, #c22f3e); }

/* ── Foot ─────────────────────────────────────────────────────────────────── */
.srm-foot {
  position: relative; z-index: 2;
  padding: clamp(12px, 2.6vw, 18px) clamp(16px, 4.5vw, 26px) clamp(14px, 3.4vw, 20px);
  min-height: 68px;
  display: flex; flex-direction: column; justify-content: center;
}
.srm-foot.in > * { animation: srm-rise 420ms var(--ease-standard) both; }
/* Same 9-10px uppercase caption ramp as the bar caps / clamp notice. */
.srm-fullmoon {
  margin: 0 0 8px; text-align: center;
  font-family: var(--font-ui); font-size: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.18em;
  color: var(--gold-5, #e6b866);
}
.srm-skip {
  margin: 0; text-align: center;
  font-family: var(--font-ui); font-size: 11px; letter-spacing: 0.14em;
  text-transform: uppercase; color: rgba(255, 255, 255, 0.28);
  animation: srm-breathe 2.6s ease-in-out infinite;
}

/* ── Keyframes ────────────────────────────────────────────────────────────── */
@keyframes srm-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes srm-panel-in {
  from { opacity: 0; transform: translateY(26px) scale(0.965); }
  to   { opacity: 1; transform: none; }
}
@keyframes srm-slam {
  0%   { opacity: 0; transform: scale(1.5); filter: blur(14px); }
  55%  { opacity: 1; filter: blur(0); }
  72%  { transform: scale(0.975); }
  100% { opacity: 1; transform: scale(1); filter: blur(0); }
}
@keyframes srm-rise {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: none; }
}
@keyframes srm-row-in {
  0%   { opacity: 0; transform: translateY(16px) scale(0.97); filter: blur(6px); }
  100% { opacity: 1; transform: none; filter: blur(0); }
}
@keyframes srm-sweep {
  from { transform: translateX(-110%); }
  to   { transform: translateX(110%); }
}
@keyframes srm-peak {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.18); text-shadow: 0 0 40px rgba(230, 184, 102, 0.75); }
  100% { transform: scale(1); }
}
@keyframes srm-breathe { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }

/* Reduced motion — the ORDER and the TIMING stay (the sequencer still schedules
   every beat); only the movement goes. Rows/verdict/bar still appear one by one,
   they just do not slide, blur, sweep or pulse. */
@media (prefers-reduced-motion: reduce) {
  .srm-overlay, .srm-panel, .srm-verdict.in, .srm-level.in, .srm-row.in, .srm-split.in, .srm-capped.in, .srm-foot.in > * {
    animation: srm-appear 1ms linear both !important;
  }
  .srm-row-sweep { display: none; }
  .srm-total.peak .srm-total-value { animation: none; }
  .srm-skip, .srm-cap-label.up { animation: none; }
  @keyframes srm-appear { from { opacity: 1; transform: none; filter: none; } to { opacity: 1; transform: none; filter: none; } }
}
`;
